import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HPButton from "@components/common/Button";

type PinPosition = "none" | "first" | "last";

interface BarRowProps {
  name: string;
  displayName: string;
  isOverridden: boolean;
  isHidden: boolean;
  pinPosition: PinPosition;
  isCustomOrder: boolean;
  showPinControl: boolean;
  showColorSwatch: boolean;
  seriesColor: string;
  isEditing: boolean;
  editValue: string;
  onToggleVisibility: () => void;
  onPinChange: (position: PinPosition) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditValueChange: (value: string) => void;
  onResetLabel: () => void;
}

const BarRow: React.FC<BarRowProps> = ({
  name,
  displayName,
  isOverridden,
  isHidden,
  pinPosition,
  isCustomOrder,
  showPinControl,
  showColorSwatch,
  seriesColor,
  isEditing,
  editValue,
  onToggleVisibility,
  onPinChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditValueChange,
  onResetLabel,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: name, disabled: !isCustomOrder });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: isHidden ? "#f5f5f5" : "white",
        opacity: isHidden ? 0.6 : 1,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {/* Drag handle — only in custom order mode */}
      {isCustomOrder && (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "#666",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}

      {/* Visibility toggle */}
      <IconButton
        size="small"
        onClick={onToggleVisibility}
        sx={{ flexShrink: 0 }}
      >
        {isHidden ? (
          <VisibilityOffIcon fontSize="small" />
        ) : (
          <VisibilityIcon fontSize="small" />
        )}
      </IconButton>

      {/* Pin control — only in non-custom sort modes */}
      {showPinControl && (
        <Select
          value={pinPosition}
          onChange={(e) => onPinChange(e.target.value as PinPosition)}
          size="small"
          variant="standard"
          disableUnderline
          disabled={isHidden}
          sx={{
            fontSize: 11,
            flexShrink: 0,
            width: 68,
            "& .MuiSelect-select": { py: 0, px: 0.5 },
          }}
        >
          <MenuItem value="none" sx={{ fontSize: 12 }}>
            No pin
          </MenuItem>
          <MenuItem value="first" sx={{ fontSize: 12 }}>
            ↑ First
          </MenuItem>
          <MenuItem value="last" sx={{ fontSize: 12 }}>
            ↓ Last
          </MenuItem>
        </Select>
      )}

      {/* Label or edit field */}
      {isEditing ? (
        <Box sx={{ flex: 1 }}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveEdit();
              if (e.key === "Escape") onCancelEdit();
            }}
            autoFocus
            className="w-full px-3 py-2 border border-[#D9D9D9] rounded-lg text-sm outline-none focus:border-[#1976d2] transition-colors"
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: isOverridden ? 600 : 400,
            }}
          >
            {displayName}
          </Typography>
          {isOverridden && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              Original: {name}
            </Typography>
          )}
        </Box>
      )}

      {/* Action buttons */}
      {isEditing ? (
        <>
          <IconButton size="small" onClick={onSaveEdit} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onCancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <IconButton
            size="small"
            onClick={onStartEdit}
            disabled={isHidden}
            sx={{ flexShrink: 0 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>

          {/* Static color swatch — visible only when Custom (per-bar) palette and bar is shown */}
          {showColorSwatch && !isHidden && (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: seriesColor,
                flexShrink: 0,
                border: "1px solid rgba(0,0,0,0.12)",
              }}
            />
          )}

          {isOverridden && !isHidden && (
            <HPButton size="small" variant="text" onClick={onResetLabel}>
              Reset
            </HPButton>
          )}
        </>
      )}
    </Box>
  );
};

export default BarRow;
